import { useMemo } from "react";

/* =========================
   COMMON HELPERS
========================= */

export const formatDate = (date) =>
  new Date(date).toLocaleDateString(
    "en-IN",
    {
      day: "2-digit",
      month: "short"
    }
  );

export const getBestResult = (item) => {

  if (!item.results?.length) {
    return null;
  }

  return Math.min(
    ...item.results.map(
      r => Number(r.result)
    )
  );

};

export const getMaxResult = (item) => {

  if (!item.results?.length) {
    return null;
  }

  return Math.max(
    ...item.results.map(
      r => Number(r.result)
    )
  );

};

/* =========================
   REACTION GRAPH
========================= */

export const useReactionGraph = (

  history,

  reactionView

) => {

  return useMemo(() => {

    const grouped = {};

    /* SIMPLE REACTION */

    (history.reaction || [])

      .forEach(item => {

        if (

          reactionView === "best" &&

          (item.wrongClicks || 0) > 0

        ) {

          return;

        }

        const key =
          formatDate(
            item.createdAt
          );

        if (!grouped[key]) {

          grouped[key] = {

            date: key,

            fullDate:
              item.createdAt,

            reaction: null,

            complex: null

          };

        }

        const rawValue =

          reactionView === "best"

            ? getBestResult(item)

            : getMaxResult(item);

        const value =

          rawValue &&
          rawValue > 0

            ? Math.round(
                rawValue * 1000
              )

            : null;

        if (

          reactionView === "best" &&

          (
            value === null ||
            value < 100
          )

        ) {

          return;

        }

        if (

          value !== null &&

          (

            grouped[key].reaction === null ||

            (

              reactionView === "best"

                ? value <
                  grouped[key].reaction

                : value >
                  grouped[key].reaction

            )

          )

        ) {

          grouped[key].reaction =
            value;

        }

      });

    /* COMPLEX REACTION */

    (history.complexReaction || [])

      .forEach(item => {

        if (

          reactionView === "best" &&

          (item.wrongClicks || 0) > 0

        ) {

          return;

        }

        const key =
          formatDate(
            item.createdAt
          );

        if (!grouped[key]) {

          grouped[key] = {

            date: key,

            fullDate:
              item.createdAt,

            reaction: null,

            complex: null

          };

        }

        const rawValue =

          reactionView === "best"

            ? getBestResult(item)

            : getMaxResult(item);

        const value =

          rawValue &&
          rawValue > 0

            ? Math.round(
                rawValue * 1000
              )

            : null;

        if (

          reactionView === "best" &&

          (
            value === null ||
            value < 100
          )

        ) {

          return;

        }

        if (

          value !== null &&

          (

            grouped[key].complex === null ||

            (

              reactionView === "best"

                ? value <
                  grouped[key].complex

                : value >
                  grouped[key].complex

            )

          )

        ) {

          grouped[key].complex =
            value;

        }

      });

    return Object
      .values(grouped)
      .filter(
        item =>
          item.reaction !== null ||
          item.complex !== null
      )
      .sort(
        (a, b) =>
          new Date(
            a.fullDate
          ) -
          new Date(
            b.fullDate
          )
      );

  }, [

    history,

    reactionView

  ]);

};
/* =========================
   WORKING MEMORY GRAPH
========================= */

export const useWorkingMemoryGraph = (

  history

) => {

  return useMemo(() => {

    return Object.values(

      (history.workingMemory || [])

        .reduce(

          (acc, item) => {

            const date =

              formatDate(

                item.createdAt

              );

            if (

              !acc[date] ||

              item.level >

              acc[date].score

            ) {

              acc[date] = {

                date,

                fullDate:

                  item.createdAt,

                score:

                  item.level || 0

              };

            }

            return acc;

          },

          {}

        )

    )

    .sort(

      (a, b) =>

        new Date(a.fullDate) -

        new Date(b.fullDate)

    );

  }, [

    history

  ]);

};

/* =========================
   SHORT TERM MEMORY GRAPH
========================= */

export const useShortMemoryGraph = (

  history

) => {

  return useMemo(() => {

    return Object.values(

      (history.shortTermMemory || [])

        .reduce(

          (acc, item) => {

            const date =

              formatDate(

                item.createdAt

              );

            if (

              !acc[date] ||

              item.level >

              acc[date].score

            ) {

              acc[date] = {

                date,

                fullDate:

                  item.createdAt,

                score:

                  item.level || 0

              };

            }

            return acc;

          },

          {}

        )

    )

    .sort(

      (a, b) =>

        new Date(a.fullDate) -

        new Date(b.fullDate)

    );

  }, [

    history

  ]);

};

/* =========================
   SEARCH SPAN GRAPH
========================= */

export const useSearchSpanGraph = (

  history

) => {

  return useMemo(() => {

    return Object.values(

      (history.searchSpan || [])

        .reduce(

          (acc, item) => {

            if (

              !item.finalNumber ||

              item.finalNumber <= 0

            ) {

              return acc;

            }

            const date =

              formatDate(

                item.createdAt

              );

            if (

              !acc[date] ||

              item.finalNumber >

              acc[date].score

            ) {

              acc[date] = {

                date,

                fullDate:

                  item.createdAt,

                score:

                  item.finalNumber

              };

            }

            return acc;

          },

          {}

        )

    )

    .sort(

      (a, b) =>

        new Date(a.fullDate) -

        new Date(b.fullDate)

    );

  }, [

    history

  ]);

};
/* =========================
   ANTICIPATION GRAPH
========================= */

export const useAnticipationGraph = (

  history

) => {

  return useMemo(() => {

    return Object.values(

      (history.anticipation || [])

        .reduce(

          (acc, item) => {

            const date =

              formatDate(

                item.createdAt

              );

            if (

              !acc[date] ||

              item.score >

              acc[date].score

            ) {

              acc[date] = {

                date,

                fullDate:

                  item.createdAt,

                score:

                  item.score || 0

              };

            }

            return acc;

          },

          {}

        )

    )

    .sort(

      (a, b) =>

        new Date(a.fullDate) -

        new Date(b.fullDate)

    );

  }, [

    history

  ]);

};

/* =========================
   CONTRAST GRAPH
========================= */

export const useContrastGraph = (

  history

) => {

  return useMemo(() => {

    return Object.values(

      (history.contrastSensitivity || [])

        .reduce(

          (acc, item) => {

            const date =

              formatDate(

                item.createdAt

              );

            if (

              !acc[date] ||

              item.finalContrast >

              acc[date].score

            ) {

              acc[date] = {

                date,

                fullDate:

                  item.createdAt,

                score:

                  item.finalContrast || 0

              };

            }

            return acc;

          },

          {}

        )

    )

    .sort(

      (a, b) =>

        new Date(a.fullDate) -

        new Date(b.fullDate)

    );

  }, [

    history

  ]);

};

/* =========================
   VISUAL ACUITY GRAPH
========================= */

const acuityMap = {

  "6/60": 1,
  "6/36": 2,
  "6/24": 3,
  "6/18": 4,
  "6/12": 5,
  "6/9": 6,
  "6/6": 7,
  "6/5": 8,
  "6/4.5": 9,
  "6/4": 10,
  "6/3.5": 11,
  "6/3": 12,
  "6/2.5": 13

};

export const useVisualAcuityGraph = (

  history

) => {

  return useMemo(() => {

    return Object.values(

      (history.visualAcuity || [])

        .reduce(

          (acc, item) => {

            const score =

              acuityMap[
                item.bothEyes
              ];

            if (

              !score ||

              item.bothEyes === "None" ||

              item.bothEyes === "nan"

            ) {

              return acc;

            }

            const date =

              formatDate(

                item.createdAt

              );

            if (

              !acc[date] ||

              score >

              acc[date].score

            ) {

              acc[date] = {

                date,

                fullDate:

                  item.createdAt,

                score

              };

            }

            return acc;

          },

          {}

        )

    )

    .sort(

      (a, b) =>

        new Date(a.fullDate) -

        new Date(b.fullDate)

    );

  }, [

    history

  ]);

};

export {

  acuityMap

};